import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadSimple, FilePdf, X } from "@phosphor-icons/react";

const CATEGORIES = ["Publication", "Preprint", "News", "Dataset", "Report", "Blog", "Other"];

export default function Collect() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", url: "", category: "", notes: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("url", form.url);
      fd.append("category", form.category);
      fd.append("notes", form.notes);
      if (file) fd.append("file", file);
      await api.post("/entries", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Entry added to input database");
      navigate("/input");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="h-full overflow-y-auto">
      <header className="h-16 flex items-center px-8 border-b border-white/5">
        <h1 className="text-xl font-bold tracking-tight">Collect Entry</h1>
      </header>
      <div className="max-w-2xl mx-auto px-8 py-10">
        <p className="text-slate-400 text-sm mb-8">Add a URL, PDF, and notes to the input database for editorial review.</p>
        <form onSubmit={submit} className="space-y-6" data-testid="collect-form">
          <div>
            <Label className="text-slate-300">Title <span className="text-red-400">*</span></Label>
            <Input data-testid="collect-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required
              className="mt-1.5 bg-[#131722] border-white/10 focus:ring-2 focus:ring-blue-500" placeholder="Entry title" />
          </div>
          <div>
            <Label className="text-slate-300">URL</Label>
            <Input data-testid="collect-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} type="url"
              className="mt-1.5 bg-[#131722] border-white/10 focus:ring-2 focus:ring-blue-500" placeholder="https://…" />
          </div>
          <div>
            <Label className="text-slate-300">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="collect-category" className="mt-1.5 bg-[#131722] border-white/10"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">PDF File</Label>
            {file ? (
              <div className="mt-1.5 flex items-center justify-between bg-[#131722] border border-white/10 rounded-md px-4 py-3">
                <span className="flex items-center gap-2 text-sm text-slate-200 truncate"><FilePdf size={18} className="text-red-400" /> {file.name}</span>
                <button type="button" data-testid="collect-remove-file" onClick={() => setFile(null)} className="text-slate-400 hover:text-red-400"><X size={16} /></button>
              </div>
            ) : (
              <label data-testid="collect-file-label" className="mt-1.5 flex flex-col items-center justify-center gap-2 border border-dashed border-slate-600 rounded-md py-8 cursor-pointer hover:border-blue-500 hover:bg-blue-500/5 transition-colors duration-200">
                <UploadSimple size={24} className="text-slate-400" />
                <span className="text-sm text-slate-400">Click to upload a PDF</span>
                <input data-testid="collect-file-input" type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
            )}
          </div>
          <div>
            <Label className="text-slate-300">Notes</Label>
            <Textarea data-testid="collect-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4}
              className="mt-1.5 bg-[#131722] border-white/10 focus:ring-2 focus:ring-blue-500" placeholder="Context, observations…" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="submit" data-testid="collect-submit" disabled={loading} className="bg-blue-600 hover:bg-blue-500 rounded-full transition-colors duration-200">
              {loading ? "Saving…" : "Add to Input Database"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => navigate(-1)} className="rounded-full text-slate-400 hover:bg-white/5">Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
